import { defineStore } from 'pinia'
import { SportsModeServiceRepository } from '@/__core__/repository/sports-mode-service.repository'
import { extendDictionary, initLocalization } from '@/__core__/localization/localization'
import { allSettled } from '@/__core__/utils/fetch'

export const useStripeStore = defineStore('stripe', {
  state: () => ({
    rawEvents: {},
    drfRetryCounter: 0,
    isPaused: false
  }),

  getters: {
    getIsPaused: state => state.isPaused,
    getShouldStopRefresh: state => state.drfRetryCounter >= 10,
    events: state => Object.values(state.rawEvents)
  },

  actions: {
    setIsPaused(paused) {
      this.isPaused = paused
    },
    getShouldCallApi() {
      // ako je mec live ili ako ima mec u buducnosti, stani ako nema nijedan mec u buducnosti
      return true // update condition when we have season
    },

    /** @param {Payload} payload */
    async fetchStripeData(payload) {
      try {
        const { languageIdentifier, stripeApiPayload, groupByCompetition, sortSample, widgetId } = payload

        const [, responseLocalization, response] = await allSettled([
          initLocalization(languageIdentifier),
          SportsModeServiceRepository.Localization.get({ languageIdentifier, widgetId }),
          SportsModeServiceRepository.StripeV2.post(stripeApiPayload)
        ])

        if (!responseLocalization) {
          throw new Error('Localization data not found')
        }

        extendDictionary(responseLocalization.data, languageIdentifier)

        if (!response.data) {
          throw new Error('Stripe data not found')
        }

        this.updateStripeEvents(response.data, groupByCompetition, sortSample)

        // schedule refresh
        return true
      } catch (error) {
        throw new Error(`[Stripe.store, fetchStripeData]: ${error.message}`)
      }
    },

    async scheduleStripeRefresh(payload) {
      try {
        const { stripeApiPayload, groupByCompetition, sortSample } = payload
        const response = await SportsModeServiceRepository.StripeV2.post(stripeApiPayload)

        this.updateStripeEvents(response.data, groupByCompetition, sortSample)
      } catch (error) {
        this.drfRetryCounter++
      }
    },

    updateStripeEvents(stripeResponse = [], groupByCompetition = false, sortSample = []) {
      // Extract, filter, and sort all events
      const allEvents = Object.entries(stripeResponse.events)
        .flatMap(([sportType, events]) =>
          events
            .filter(event => event.status.event !== 'postponed' && event.status.event !== 'cancelled') // Remove postponed events
            .map(event => ({ ...event, sportType }))
        )
        .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))

      if (!allEvents) return

      if (groupByCompetition) {
        const groupedEvents = {}

        // Group events by season ID
        allEvents.forEach(event => {
          const {
            season: { id: seasonId }
          } = event

          if (!groupedEvents[seasonId]) {
            groupedEvents[seasonId] = []
          }
          groupedEvents[seasonId].push(event)
        })

        // Create a map for faster season ID lookups
        const seasonIdSet = sortSample.map(event => event.seasonId)

        seasonIdSet.forEach(seasonId => {
          if (groupedEvents[seasonId]) {
            const [firstEvent] = groupedEvents[seasonId]
            const { competition } = firstEvent
            // Create a category event for the competition
            this.rawEvents[`${seasonId}_category`] = {
              id: `${seasonId}_category`,
              kind: 'category',
              name: competition.name,
              ...competition
            }

            // Add events for the competition
            groupedEvents[seasonId].forEach(event => {
              this.rawEvents[event.id] = {
                ...event,
                kind: 'event'
              }
            })
          }
        })
      } else {
        // commented code is used if there is need for competition image before events
        // Add events without grouping by competition
        // let lastAddedCompetitionId = null

        allEvents.forEach(event => {
          // const { id, name } = event.competition

          // // Check if this is the first event for the competition
          // if (id !== lastAddedCompetitionId) {
          //   // Create a category event for the competition
          //   this.rawEvents[`${id}_${index}_category`] = {
          //     id: `${id}_${index}_category`,
          //     kind: 'category',
          //     name: name,
          //     ...event.competition
          //   }

          //   lastAddedCompetitionId = id
          // }

          this.rawEvents[event.id] = {
            ...event,
            kind: 'event'
          }
        })
      }
    }
  }
})
